/**
 * 搜索供应商管理 API 服务
 *
 * 封装搜索供应商管理相关的 REST API 调用，用于管理后台。
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * 管理 API 请求封装：附带用户体系 Bearer token（is_admin 用户认证兜底），
 * 后端优先校验 admin_token / access_token Cookie
 */
async function adminFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers || {});
  const userToken =
    sessionStorage.getItem('access_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('access_token');
  if (userToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${userToken}`);
  }
  return fetch(input, { ...init, headers, credentials: init.credentials ?? 'include' });
}

// ========================================
// 类型定义
// ========================================

export interface SearchProviderInfo {
  provider_name: string;
  display_name: string;
  provider_type: string;
  enabled: boolean;
  priority: number;
  weight: number;
  search_types: string[];
  key_count: number;
  enabled_key_count: number;
}

export interface SearchApiKeyInfo {
  key_index: number;
  key_suffix: string;
  status: string;
  quota: Record<string, unknown>;
  error_count: number;
  disabled_reason: string | null;
  disabled_time: string | null;
}

export interface SearchOverviewData {
  total_providers: number;
  enabled_providers: number;
  total_keys: number;
  enabled_keys: number;
  healthy_providers: number;
  circuit_open_count: number;
  last_loaded: string | null;
}

export interface SearchQuotaItem {
  provider_name: string;
  key_index: number;
  quota_type: string;
  used: number;
  limit: number;
  remaining: number;
  usage_percent: number;
}

export interface SearchProviderStats {
  provider_name: string;
  display_name: string;
  enabled: boolean;
  priority: number;
  weight: number;
  key_count: number;
  enabled_key_count: number;
  total_checks: number;
  total_failures: number;
  avg_latency_ms: number;
  circuit_breakers: Array<{
    provider_name: string;
    key_index: number;
    state: string;
    failure_count: number;
    last_failure_time: string | null;
    opened_at: string | null;
  }>;
}

export interface SearchErrorItem {
  provider_name: string;
  key_index: number;
  error_count: number;
  total_failures: number;
  failure_rate: number;
  last_error: string | null;
  last_error_time: string | null;
}

export interface SearchLatencyItem {
  provider_name: string;
  key_index: number;
  last_latency_ms: number;
  avg_latency_ms: number;
  total_checks: number;
}

export interface SearchTrendItem {
  provider_name: string;
  display_name: string;
  total_checks: number;
  total_failures: number;
  avg_latency_ms: number;
  enabled: boolean;
}

export interface SearchLiveKey {
  key_index: number;
  available: boolean;
  concurrency: number;
  rpm: number;
  last_latency_ms: number;
  avg_latency_ms: number;
  circuit_state: string;
  last_error: string | null;
}

export interface SearchLiveProvider {
  provider_name: string;
  display_name: string;
  enabled: boolean;
  keys: SearchLiveKey[];
}

export interface SearchLiveData {
  success: boolean;
  timestamp: string;
  providers: SearchLiveProvider[];
  summary: {
    total_concurrency: number;
    total_rpm: number;
    healthy_keys: number;
    error_keys: number;
  };
}

export interface SearchHealthKey {
  key_index: number;
  available: boolean;
  total_checks: number;
  total_failures: number;
  error_count: number;
  avg_latency_ms: number;
  last_latency_ms: number;
  last_error: string | null;
  last_error_time: number;
  last_check_time: number;
  failure_rate: number;
}

export interface SearchSettingsData {
  settings: Record<string, unknown>;
  last_loaded: string | null;
  settings_path: string | null;
}

export interface SearchProviderCreate {
  provider_name: string;
  display_name?: string;
  provider_type: string;
  base_url?: string;
  description?: string;
  priority?: number;
  weight?: number;
  search_types?: string[];
  key_selection_mode?: string;
  timeout?: number;
  adapter_config?: Record<string, unknown>;
}

export interface SearchProviderUpdate {
  display_name?: string;
  description?: string;
  enabled?: boolean;
  priority?: number;
  weight?: number;
  search_types?: string[];
  key_selection_mode?: string;
  base_url?: string;
  timeout?: number;
  adapter_config?: Record<string, unknown>;
}

export interface SearchApiKeyCreate {
  api_key: string;
  quota?: Record<string, unknown>;
}

export interface SearchApiKeyUpdate {
  api_key?: string;
  quota?: Record<string, unknown>;
}

export interface SearchQuotaResetRequest {
  provider_name?: string;
  key_index?: number;
  reset_all?: boolean;
}

export interface UserQuotaQueryRequest {
  user_id: number;
  date?: string;
}

export interface UserQuotaUsageRecord {
  id: number;
  user_id: number;
  workspace_id: number;
  query: string;
  search_type: string;
  results_count: number;
  created_at: string;
}

export interface UserQuotaQueryResponse {
  user_id: number;
  date: string;
  daily_limit: number;
  used: number;
  remaining: number;
  records: UserQuotaUsageRecord[];
}

// ========================================
// 通用请求工具
// ========================================

function getAuthHeaders(token?: string): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.detail || errorData.message || '请求失败';

    if (response.status === 401) {
      throw new Error(`未授权: ${message}`);
    }
    if (response.status === 403) {
      throw new Error(`没有权限: ${message}`);
    }
    if (response.status === 404) {
      throw new Error(`资源不存在: ${message}`);
    }

    throw new Error(message);
  }

  return response.json();
}

// ========================================
// API 客户端
// ========================================

export const searchAdminApi = {
  // ========== 配置管理 ==========

  async getSettings(token?: string): Promise<SearchSettingsData> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/search/settings`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<SearchSettingsData>(response);
  },

  async updateSettings(
    token: string | undefined,
    settings: Record<string, unknown>
  ): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/search/settings`, {
      method: 'PUT',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify({ settings }),
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  // ========== 供应商 CRUD ==========

  async listProviders(token?: string): Promise<SearchProviderInfo[]> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/settings/providers`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<SearchProviderInfo[]>(response);
  },

  async createProvider(
    token: string | undefined,
    data: SearchProviderCreate
  ): Promise<{ success: boolean; provider: Record<string, unknown> }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/settings/providers`,
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );
    return handleResponse<{ success: boolean; provider: Record<string, unknown> }>(
      response
    );
  },

  async updateProvider(
    token: string | undefined,
    name: string,
    data: SearchProviderUpdate
  ): Promise<{ success: boolean; provider: Record<string, unknown> }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/settings/providers/${encodeURIComponent(name)}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(token),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );
    return handleResponse<{ success: boolean; provider: Record<string, unknown> }>(
      response
    );
  },

  async deleteProvider(
    token: string | undefined,
    name: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/settings/providers/${encodeURIComponent(name)}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  // ========== API 密钥管理 ==========

  async listApiKeys(
    token: string | undefined,
    providerName: string
  ): Promise<SearchApiKeyInfo[]> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/settings/providers/${encodeURIComponent(providerName)}/keys`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<SearchApiKeyInfo[]>(response);
  },

  async addApiKey(
    token: string | undefined,
    providerName: string,
    data: SearchApiKeyCreate
  ): Promise<{ success: boolean; key: Record<string, unknown> }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/settings/providers/${encodeURIComponent(providerName)}/keys`,
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );
    return handleResponse<{ success: boolean; key: Record<string, unknown> }>(
      response
    );
  },

  async updateApiKey(
    token: string | undefined,
    providerName: string,
    keyIndex: number,
    data: SearchApiKeyUpdate
  ): Promise<{ success: boolean; key: Record<string, unknown> }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/settings/providers/${encodeURIComponent(providerName)}/keys/${keyIndex}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(token),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );
    return handleResponse<{ success: boolean; key: Record<string, unknown> }>(
      response
    );
  },

  async deleteApiKey(
    token: string | undefined,
    providerName: string,
    keyIndex: number
  ): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/settings/providers/${encodeURIComponent(providerName)}/keys/${keyIndex}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  async enableApiKey(
    token: string | undefined,
    providerName: string,
    keyIndex: number
  ): Promise<{ success: boolean; key: Record<string, unknown> }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/settings/providers/${encodeURIComponent(providerName)}/keys/${keyIndex}/enable`,
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<{ success: boolean; key: Record<string, unknown> }>(
      response
    );
  },

  async disableApiKey(
    token: string | undefined,
    providerName: string,
    keyIndex: number,
    reason?: string
  ): Promise<{ success: boolean; key: Record<string, unknown> }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/settings/providers/${encodeURIComponent(providerName)}/keys/${keyIndex}/disable`,
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        credentials: 'include',
        body: JSON.stringify(reason ? { reason } : {}),
      }
    );
    return handleResponse<{ success: boolean; key: Record<string, unknown> }>(
      response
    );
  },

  // ========== 配额管理 ==========

  async getQuotaUsage(token?: string): Promise<{
    success: boolean;
    quotas: Record<string, Record<string, SearchQuotaItem[]>>;
  }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/quota/usage`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  async getKeyQuotaUsage(
    token: string | undefined,
    providerName: string,
    keyIndex: number
  ): Promise<{ success: boolean; quotas: SearchQuotaItem[] }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/quota/usage/${encodeURIComponent(providerName)}/${keyIndex}`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  async resetQuota(
    token: string | undefined,
    data: SearchQuotaResetRequest
  ): Promise<{ success: boolean; message: string; count?: number }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/quota/reset`,
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );
    return handleResponse(response);
  },

  async flushQuota(
    token?: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/quota/flush`,
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  async queryUserQuota(
    token: string | undefined,
    request: UserQuotaQueryRequest
  ): Promise<{ success: boolean; data: UserQuotaQueryResponse }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/user-quota`,
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        credentials: 'include',
        body: JSON.stringify(request),
      }
    );
    return handleResponse(response);
  },

  // ========== 健康检查 ==========

  async getHealthStatus(
    token?: string,
    providerName?: string
  ): Promise<{
    success: boolean;
    health: Record<string, SearchHealthKey[]>;
  }> {
    const params = new URLSearchParams();
    if (providerName) params.append('provider_name', providerName);
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/health?${params.toString()}`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  async triggerHealthCheck(
    token: string | undefined,
    providerName?: string,
    keyIndex?: number
  ): Promise<{
    success: boolean;
    results?: Array<Record<string, unknown>>;
    result?: Record<string, unknown>;
    total?: number;
    success_count?: number;
    failed_count?: number;
  }> {
    const params = new URLSearchParams();
    if (providerName) params.append('provider_name', providerName);
    if (keyIndex !== undefined) params.append('key_index', keyIndex.toString());
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/health/check?${params.toString()}`,
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  async validateApiKey(
    token: string | undefined,
    providerName: string,
    apiKey: string
  ): Promise<{ success: boolean; result: Record<string, unknown> }> {
    const params = new URLSearchParams();
    params.append('api_key', apiKey);
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/health/validate/${encodeURIComponent(providerName)}?${params.toString()}`,
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  async resetCircuitBreaker(
    token: string | undefined,
    providerName: string,
    keyIndex: number
  ): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/health/circuit-breaker/${encodeURIComponent(providerName)}/${keyIndex}/reset`,
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  // ========== 统计 ==========

  async getOverviewStats(
    token?: string
  ): Promise<{ success: boolean; data: SearchOverviewData }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/stats/overview`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  async getTrends(
    token?: string,
    hours?: number
  ): Promise<{
    success: boolean;
    data: SearchTrendItem[];
    hours: number;
  }> {
    const params = new URLSearchParams();
    if (hours) params.append('hours', hours.toString());
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/stats/trends?${params.toString()}`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  async getProviderStats(
    token?: string
  ): Promise<{ success: boolean; data: SearchProviderStats[] }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/stats/providers`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  async getErrorStats(
    token?: string
  ): Promise<{ success: boolean; data: SearchErrorItem[] }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/stats/errors`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  async getLatencyStats(
    token?: string
  ): Promise<{ success: boolean; data: SearchLatencyItem[] }> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/stats/latency`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse(response);
  },

  // ========== 实时监控 ==========

  async getLiveMonitoring(token?: string): Promise<SearchLiveData> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/monitoring/live`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<SearchLiveData>(response);
  },

  // ========== 配置导出 ==========

  async exportJson(
    token?: string
  ): Promise<Record<string, unknown>> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/export/json`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<Record<string, unknown>>(response);
  },

  async exportCsv(token?: string): Promise<Blob> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/search/export/csv`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    if (!response.ok) {
      throw new Error('导出失败');
    }
    return response.blob();
  },
};

export default searchAdminApi;
